import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not configured. Using mock data.');
}

export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// Categories
export async function fetchCategories() {
  if (!supabase) return { data: null, error: new Error('Supabase not configured') };
  
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('name_de', { ascending: true });
  
  return { data, error };
}

// Featured Products (by specific IDs)
export async function fetchFeaturedProducts(productIds) {
  if (!supabase) return { data: null, error: new Error('Supabase not configured') };
  
  const { data: products, error } = await supabase
    .from('products')
    .select(`
      *,
      category:categories(*)
    `)
    .in('id', productIds)
    .eq('is_active', true);
  
  if (error || !products) return { data: null, error };
  
  // Fetch primary images
  const { data: images } = await supabase
    .from('product_images')
    .select('*')
    .in('product_id', productIds)
    .eq('is_primary', true);
  
  const imageMap = {};
  if (images) {
    images.forEach(img => {
      imageMap[img.product_id] = img.image_url;
    });
  }
  
  // Add image_url and sort by the order of productIds
  const productsWithImages = productIds
    .map(id => products.find(p => p.id === id))
    .filter(p => p && imageMap[p.id])
    .map(p => ({
      ...p,
      image_url: imageMap[p.id],
      dimensions: formatDimensions(p.length_cm, p.depth_cm, p.height_cm)
    }));
  
  return { data: productsWithImages, error: null };
}

// Products - join with primary image
export async function fetchProducts(categorySlug = null) {
  if (!supabase) return { data: null, error: new Error('Supabase not configured') };
  
  // First get category ID if slug provided
  let categoryId = null;
  if (categorySlug) {
    const { data: category } = await supabase
      .from('categories')
      .select('id')
      .eq('slug', categorySlug)
      .single();
    
    if (category) {
      categoryId = category.id;
    } else {
      return { data: [], error: null };
    }
  }
  
  // Fetch products with category
  let query = supabase
    .from('products')
    .select(`
      *,
      category:categories(*)
    `)
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  
  if (categoryId) {
    query = query.eq('category_id', categoryId);
  }
  
  const { data: products, error } = await query;
  
  if (error || !products) return { data: null, error };
  
  // Fetch primary images for all products
  const productIds = products.map(p => p.id);
  const { data: images } = await supabase
    .from('product_images')
    .select('*')
    .in('product_id', productIds)
    .eq('is_primary', true);
  
  // Map images to products
  const imageMap = {};
  if (images) {
    images.forEach(img => {
      imageMap[img.product_id] = img.image_url;
    });
  }
  
  // Add image_url to products
  const productsWithImages = products.map(p => ({
    ...p,
    image_url: imageMap[p.id] || null,
    // Format dimensions
    dimensions: formatDimensions(p.length_cm, p.depth_cm, p.height_cm)
  }));
  
  // Filter out products without images
  const productsWithValidImages = productsWithImages.filter(p => p.image_url);
  
  return { data: productsWithValidImages, error: null };
}

export async function fetchProductBySlug(slug) {
  if (!supabase) return { data: null, error: new Error('Supabase not configured') };
  
  const { data: product, error } = await supabase
    .from('products')
    .select(`
      *,
      category:categories(*)
    `)
    .eq('slug', slug)
    .eq('is_active', true)
    .single();
  
  if (error || !product) return { data: null, error };
  
  // Get primary image
  const { data: primaryImage } = await supabase
    .from('product_images')
    .select('image_url')
    .eq('product_id', product.id)
    .eq('is_primary', true)
    .single();
  
  return { 
    data: {
      ...product,
      image_url: primaryImage?.image_url || null,
      dimensions: formatDimensions(product.length_cm, product.depth_cm, product.height_cm)
    }, 
    error: null 
  };
}

// Product Images
export async function fetchProductImages(productId) {
  if (!supabase) return { data: null, error: new Error('Supabase not configured') };
  
  const { data, error } = await supabase
    .from('product_images')
    .select('*')
    .eq('product_id', productId)
    .order('sort_order', { ascending: true });
  
  return { data, error };
}

// Helper to format dimensions
function formatDimensions(length, depth, height) {
  const parts = [];
  if (length) parts.push(`L: ${length} cm`);
  if (depth) parts.push(`D: ${depth} cm`);
  if (height) parts.push(`H: ${height} cm`);
  return parts.length > 0 ? parts.join(' × ') : null;
}

// General Contact Form Submission (for /contact page)
export async function submitGeneralContact(formData) {
  const webhookUrl = process.env.REACT_APP_GENERAL_CONTACT_WEBHOOK_URL;
  
  if (!webhookUrl) {
    console.warn('General contact webhook URL not configured');
    return { success: false, error: 'Webhook not configured' };
  }
  
  try {
    // Use no-cors mode for Pabbly webhooks (they don't support CORS)
    // This means we can't read the response, but the data will be sent
    await fetch(webhookUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain', // Required for no-cors with body
      },
      body: JSON.stringify({
        type: 'general_contact',
        ...formData,
        submitted_at: new Date().toISOString(),
        source: 'website_contact_page'
      }),
    });
    
    // With no-cors, we can't read response status, so assume success if no error thrown
    return { success: true };
  } catch (error) {
    console.error('General contact submission error:', error);
    return { success: false, error: error.message };
  }
}

// Product Inquiry Submission (for single product or bulk inquiries)
export async function submitProductInquiry(formData) {
  const webhookUrl = process.env.REACT_APP_PRODUCT_INQUIRY_WEBHOOK_URL;
  
  if (!webhookUrl) {
    console.warn('Product inquiry webhook URL not configured');
    return { success: false, error: 'Webhook not configured' };
  }
  
  try {
    // Use no-cors mode for Pabbly webhooks (they don't support CORS)
    await fetch(webhookUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain', // Required for no-cors with body
      },
      body: JSON.stringify({
        ...formData,
        submitted_at: new Date().toISOString(),
        source: 'website_product_inquiry'
      }),
    });
    
    // With no-cors, we can't read response status, so assume success if no error thrown
    return { success: true };
  } catch (error) {
    console.error('Product inquiry submission error:', error);
    return { success: false, error: error.message };
  }
}
